import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OnboardingStepper from '../components/OnboardingStepper';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import FramedAvatar from '../components/FramedAvatar';
import InvestmentsManager from '../components/InvestmentsManager';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Camera,
  Save,
  Mail,
  Bell,
  Palette,
  Upload,
  X,
  Check,
  Anchor,
  MapPin,
  Lock,
  Eye,
  EyeOff,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import NotificationsAndInstallSettings from '../components/NotificationsAndInstallSettings';
import ProfileShowcase from '../components/profile/ProfileShowcase';
import AvatarCropper from '../components/AvatarCropper';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';

const ProfilePage = () => {
  const { user, api, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    avatar_url: '',
    notification_preferences: { email: true, in_app: true, push: true },
    city: '',
    show_location: false
  });
  const [previewAvatar, setPreviewAvatar] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/auth/profile');
      setProfile({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        bio: response.data.bio || '',
        avatar_url: response.data.avatar_url || '',
        notification_preferences: { email: true, in_app: true, push: true, ...(response.data.notification_preferences || {}) },
        city: response.data.city || '',
        show_location: response.data.show_location || false
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.put('/auth/profile', {
        first_name: profile.first_name,
        last_name: profile.last_name,
        bio: profile.bio,
        notification_preferences: profile.notification_preferences,
        city: profile.city,
        show_location: profile.show_location
      });
      
      // Update local user context
      if (updateUser) {
        updateUser({
          ...user,
          first_name: profile.first_name,
          last_name: profile.last_name,
          bio: profile.bio,
          avatar_url: profile.avatar_url
        });
      }
      
      toast.success(isOnboarding ? 'Profil enregistré ! Bienvenue dans l\'équipage 🎉' : 'Profil mis à jour !');
      if (isOnboarding) {
        // After first-time setup → land on dashboard
        setTimeout(() => navigate('/'), 800);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.current_password) {
      toast.error('Entrez votre mot de passe actuel');
      return;
    }
    if (!passwordData.new_password) {
      toast.error('Entrez un nouveau mot de passe');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Mot de passe modifié avec succès !');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordSection(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPEG, PNG, GIF ou WebP.');
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 25 Mo)');
      return;
    }

    // Recadrage avant envoi : l'utilisateur cadre son visage dans le cercle
    setCropFile(file);
  };

  const handleCropConfirm = async (cropped) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', cropped);

      const response = await api.post('/auth/upload-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfile((p) => ({ ...p, avatar_url: response.data.avatar_url }));
      setCropFile(null);

      if (updateUser) {
        updateUser({ ...user, avatar_url: response.data.avatar_url });
      }

      toast.success('Photo de profil mise à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setRemoving(true);
    try {
      await api.delete('/auth/avatar');
      setProfile((p) => ({ ...p, avatar_url: '' }));
      if (updateUser) {
        updateUser({ ...user, avatar_url: null });
      }
      toast.success('Photo retirée — tes initiales sont de retour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de retirer la photo');
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          {!isOnboarding && (
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden"
              style={{
                border: '1.5px solid hsl(var(--gold))',
                boxShadow: '0 1px 3px rgba(40, 28, 18, 0.08), inset 0 0 0 2px hsl(var(--paper))',
                padding: '5px',
              }}
            >
              <img src="/logo-192.png" alt="Odys" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl">{isOnboarding ? 'Bienvenue dans l\'Équipage' : 'Mon Profil'}</h1>
              <p className="text-sm text-muted-foreground">{isOnboarding ? 'Étape finale — personnalise ton profil' : 'Personnalise ton compte'}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Onboarding stepper (Circle-style 1/3 → 2/3 → 3/3) */}
        {isOnboarding && <OnboardingStepper step={2} />}

        {/* Onboarding banner — first-time user welcome (Circle-style) */}
        {isOnboarding && (
          <div
            className="dossier-card relative overflow-hidden"
            style={{ padding: '1.75rem', background: 'linear-gradient(160deg, #FFFFFF 0%, hsl(var(--gold-bg)) 100%)' }}
            data-testid="profile-onboarding-banner"
          >
            <span style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, hsl(var(--gold-dark)), hsl(var(--gold)) 50%, hsl(var(--gold-dark)))' }} />
            <div className="flex items-start gap-4">
              <div className="icon-circle is-gold flex-shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="dossier-label" style={{ margin: 0 }}>Bienvenue à bord</p>
                <h2 className="font-serif text-xl font-bold text-ink mt-1 mb-2 filet-gold">
                  Personnalise ton profil
                </h2>
                <p className="text-sm italic text-muted-dossier mt-3 mb-0">
                  Ajoute ta photo, complète ta bio, choisis ta ville — les autres membres
                  pourront te reconnaître et te contacter. <strong className="text-ink not-italic">Cela prend moins de 2 minutes.</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Showcase: progression, stats, trophies, timeline (hidden during onboarding to keep focus) */}
        {!isOnboarding && <ProfileShowcase />}

        {/* Avatar Section */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Photo de profil
            </CardTitle>
            <CardDescription>Clique sur ta photo pour la modifier</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <div className="relative group">
              <div className="cursor-pointer" onClick={handleAvatarClick} data-testid="avatar-trigger">
                <FramedAvatar
                  src={previewAvatar || profile.avatar_url}
                  fallback={getInitials(profile.first_name, profile.last_name)}
                  frame={user?.profile_frame}
                  role={user?.role}
                  seniority={user?.seniority}
                  size="profile"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  {uploading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                </div>
              </div>

              {profile.avatar_url && !uploading && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setConfirmRemove(true); }}
                  className="absolute bottom-0 right-0 w-10 h-10 rounded-full flex items-center justify-center bg-white text-destructive shadow-md border border-border opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity md:opacity-0 max-md:opacity-100"
                  title="Retirer la photo"
                  aria-label="Retirer la photo"
                  data-testid="avatar-remove-button"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleFileChange}
                data-testid="avatar-input"
              />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              JPEG, PNG, GIF ou WebP — tu pourras cadrer ton visage avant l'envoi
            </p>
          </CardContent>
        </Card>

        <AvatarCropper
          file={cropFile}
          open={!!cropFile}
          busy={uploading}
          onCancel={() => setCropFile(null)}
          onConfirm={handleCropConfirm}
        />

        <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
          <AlertDialogContent data-testid="avatar-remove-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Retirer ta photo de profil ?</AlertDialogTitle>
              <AlertDialogDescription>
                Tes initiales reprendront leur place. Tu pourras remettre une photo à tout moment.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="avatar-remove-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); handleRemoveAvatar(); }}
                disabled={removing}
                data-testid="avatar-remove-confirm"
              >
                {removing ? 'Suppression…' : 'Retirer la photo'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Profile Info */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Anchor className="w-5 h-5 text-primary" />
              Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Prénom</Label>
                <Input
                  id="first_name"
                  value={profile.first_name}
                  onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                  placeholder="Ton prénom"
                  className="bg-secondary/50"
                  data-testid="first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nom</Label>
                <Input
                  id="last_name"
                  value={profile.last_name}
                  onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                  placeholder="Ton nom"
                  className="bg-secondary/50"
                  data-testid="last-name-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value.slice(0, 500) })}
                placeholder="Présente-toi à la communauté... Ton parcours, tes objectifs, ce qui te motive !"
                className="bg-secondary/50"
                rows={4}
                data-testid="bio-input"
              />
              <p className="text-xs text-muted-foreground text-right">{profile.bio?.length || 0}/500</p>
            </div>

            <div className="p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{user.email}</p>
                  <p className="text-xs text-muted-foreground">Adresse email (non modifiable)</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-secondary/30 rounded-lg">
              <Badge className="bg-primary/20 text-primary">{user.role}</Badge>
              <Badge variant="outline">Niveau {user.level || 1}</Badge>
              <Badge variant="outline">{user.points || 0} pts</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Localisation
            </CardTitle>
            <CardDescription>Apparais sur la carte des membres de la communauté</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="city">Ta ville</Label>
              <Input
                id="city"
                value={profile.city}
                onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                placeholder="Ex: Paris, Lyon, Marseille..."
                className="bg-secondary/50"
                data-testid="city-input"
              />
              <p className="text-xs text-muted-foreground">
                Ta ville sera visible sur la carte des membres
              </p>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Afficher ma localisation</p>
                  <p className="text-sm text-muted-foreground">Apparaître sur la carte des membres</p>
                </div>
              </div>
              <Switch
                checked={profile.show_location}
                onCheckedChange={(checked) => setProfile({
                  ...profile,
                  show_location: checked
                })}
                data-testid="show-location-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Investments Manager */}
        <InvestmentsManager api={api} />

        {/* Notifications */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Notifications
            </CardTitle>
            <CardDescription>Choisis comment tu veux être notifié</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Notifications par email</p>
                  <p className="text-sm text-muted-foreground">Reçois les félicitations, rappels et résumés par email</p>
                </div>
              </div>
              <Switch
                checked={profile.notification_preferences?.email}
                onCheckedChange={(checked) => setProfile({
                  ...profile,
                  notification_preferences: { ...profile.notification_preferences, email: checked }
                })}
                data-testid="email-notifications-switch"
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Notifications in-app</p>
                  <p className="text-sm text-muted-foreground">Notifications dans l'application</p>
                </div>
              </div>
              <Switch
                checked={profile.notification_preferences?.in_app}
                onCheckedChange={(checked) => setProfile({
                  ...profile,
                  notification_preferences: { ...profile.notification_preferences, in_app: checked }
                })}
                data-testid="inapp-notifications-switch"
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Notifications push</p>
                  <p className="text-sm text-muted-foreground">Alertes sur ton téléphone ou ton navigateur</p>
                </div>
              </div>
              <Switch
                checked={profile.notification_preferences?.push !== false}
                onCheckedChange={(checked) => setProfile({
                  ...profile,
                  notification_preferences: { ...profile.notification_preferences, push: checked }
                })}
                data-testid="push-notifications-switch"
              />
            </div>

            <div className="pt-2 border-t border-border/50">
              <NotificationsAndInstallSettings />
            </div>
          </CardContent>
        </Card>

        {/* Change Password Section — hidden during onboarding (user just set their password) */}
        {!isOnboarding && (
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-orange-500" />
              Sécurité
            </CardTitle>
            <CardDescription>Modifier votre mot de passe</CardDescription>
          </CardHeader>
          <CardContent>
            {!showPasswordSection ? (
              <Button 
                variant="outline" 
                onClick={() => setShowPasswordSection(true)}
                className="w-full"
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Changer mon mot de passe
              </Button>
            ) : (
              <div className="space-y-4">
                {/* Current Password */}
                <div className="space-y-2">
                  <Label>Mot de passe actuel</Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      placeholder="Entrez votre mot de passe actuel"
                      data-testid="current-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                {/* New Password */}
                <div className="space-y-2">
                  <Label>Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      placeholder="Minimum 6 caractères"
                      data-testid="new-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label>Confirmer le nouveau mot de passe</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Confirmez votre nouveau mot de passe"
                    data-testid="confirm-password-input"
                  />
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPasswordSection(false);
                      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    }}
                    className="flex-1"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                    data-testid="submit-password-btn"
                  >
                    {changingPassword ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Valider
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Save Button */}
        <Button 
          className="w-full bg-primary text-primary-foreground gold-glow" 
          size="lg"
          onClick={handleSave}
          disabled={loading}
          data-testid="save-profile-btn"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
          ) : isOnboarding ? (
            <ArrowRight className="w-5 h-5 mr-2" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          {isOnboarding ? 'Continuer vers le tableau de bord' : 'Enregistrer les modifications'}
        </Button>
      </main>
    </div>
  );
};

export default ProfilePage;
